import { EDocumentStatus, EFinancingProgram } from '@if/core/types'
import {
  Add as AddIcon,
  Merge,
  NotificationsActive as NotificationActiveIcon,
  FindInPage as ReviewDocumentsIcon,
  Send as SendIcon,
} from '@mui/icons-material'
import {
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useCreditApplicationData } from '@src/contexts/CreditApplicationContext'
import {
  useCreateRequiredDocuments,
  useDeleteRequiredDocument,
  useQuickMerge,
  useSendDocuments,
  useUpdateRequiredDocument,
} from '@src/data/api/document/document-api'
import { useSendRequiredDocumentsMessage } from '@src/data/api/message/message-api'
import {
  useAreAllDocumentsRequiredApproved,
  useSortedDocumentsByStatus,
  useSortedRequiredDocumentsByStatus,
} from '@src/data/selectors/document-selectors'
import { User } from '@src/data/types'
import { IFinanceBaseWorksheet } from '@src/data/types/IFinanceBaseWorksheetSchema'
import { reportErrorToConsole } from '@src/services/error-logger'
import { isEmpty } from 'lodash-es'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Document, DocumentRequired } from '../../../data/types/DocumentSchema'
import DeleteDocumentRequestDialog from './DeleteDocumentRequestDialog'
import EditDocumentDialog from './EditDocumentDialog'
import DocumentTableRow, { DocumentDialog } from './documentTableRow'
import DocumentsRequestDialog from './documentsRequestDialog'
import SendDocumentsDialog from './sendDocumentsDialog'

interface Props {
  creditApplicationId: string
  financingProgramId: EFinancingProgram
  editDisabled: boolean
  user: User | null
  worksheet: IFinanceBaseWorksheet | null
}

const DocumentList = ({ creditApplicationId, financingProgramId, editDisabled, user, worksheet }: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [sendRequiredDocumentsMessage, isSendingRequiredDocumentsMessage] = useSendRequiredDocumentsMessage()
  const [openDialog, setOpenDialog] = useState<DocumentDialog>(DocumentDialog.None)
  const [editRequiredDocument, setEditRequiredDocument] = useState<DocumentRequired>()
  const [requiredDocumentToDelete, setRequiredDocumentToDelete] = useState<DocumentRequired | null>()
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>([])
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [fileList, setFileList] = useState<File[]>([])

  const [createRequiredDocuments, isCreatingRequiredDocument] = useCreateRequiredDocuments()
  const [updateRequiredDocument] = useUpdateRequiredDocument()
  const [deleteRequiredDocument] = useDeleteRequiredDocument()
  const [sendDocuments] = useSendDocuments()
  const [quickMerge, isQuickMerging] = useQuickMerge()

  const closeDialog = useCallback(() => setOpenDialog(DocumentDialog.None), [setOpenDialog])

  const {
    documents: docsFromContext,
    requiredDocuments: reqDocsFromContext,
    refetch: refetchFullCreditApp,
  } = useCreditApplicationData()
  const documents = useSortedDocumentsByStatus(docsFromContext)
  const documentsRequested = useSortedRequiredDocumentsByStatus(docsFromContext, reqDocsFromContext)
  const areAllDocumentsRequiredApproved = useAreAllDocumentsRequiredApproved(docsFromContext, reqDocsFromContext)

  const isLoading = false

  const handleUpdateDocumentRequest = useCallback(
    async (data: DocumentRequired) => {
      await updateRequiredDocument({ data }).catch(reportErrorToConsole)
      closeDialog()
    },
    [closeDialog, updateRequiredDocument],
  )

  const handleDocumentChecked = (documentId: string) => (e: { target: { checked: boolean } }) => {
    setSelectedDocumentIds((prevState) => {
      if (e.target.checked) {
        return [...prevState, documentId]
      }
      return prevState.filter((i) => i !== documentId)
    })
  }

  const handleDocumentRequest = useCallback(
    async (data: DocumentRequired[]) => {
      const newRequests: DocumentRequired[] = []

      data.forEach((newRequest) => {
        const existing = documentsRequested.find(
          (r) =>
            r.typeId === newRequest.typeId &&
            r.applicantType === newRequest.applicantType &&
            r.subKey === newRequest.subKey,
        )
        if (!existing) newRequests.push(newRequest)
      })

      if (newRequests.length > 0)
        await createRequiredDocuments({
          data: newRequests,
          creditApplicationId,
          financingProgramId,
        }).catch(reportErrorToConsole)

      closeDialog()
    },
    [creditApplicationId, closeDialog, createRequiredDocuments, documentsRequested, financingProgramId],
  )

  const handleDeleteDocumentRequest = useCallback(async () => {
    if (requiredDocumentToDelete) {
      await deleteRequiredDocument({
        data: requiredDocumentToDelete,
        financingProgramId,
      }).catch(reportErrorToConsole)
      setRequiredDocumentToDelete(null)
    }

    closeDialog()
  }, [closeDialog, deleteRequiredDocument, requiredDocumentToDelete, financingProgramId])

  const closeDeleteDocumentRequestDialog = useCallback(() => {
    setOpenDialog(DocumentDialog.None)
    setRequiredDocumentToDelete(null)
  }, [setOpenDialog])

  const sendDocs = useCallback(
    async (documentList: Document[], files: File[]) => {
      setIsUploading(true)
      let interval: number

      try {
        const existingDocIds = documents.map((d) => d.id)
        const docsWithToReceive = await sendDocuments({
          documents: documentList,
          files,
          creditApplicationId,
          financingProgramId,
        })
        const newIds = docsWithToReceive.map((d) => d.id).filter((id) => !existingDocIds.includes(id))
        closeDialog()
        setFileList([])

        if (docsWithToReceive.length > 0) {
          let timesRun = 0
          interval = window.setInterval(() => {
            if (timesRun < 5) {
              timesRun += 1
              refetchFullCreditApp()
                .then((ret) => {
                  const receivedIds = ret.data?.documents?.map((d) => d.id) ?? []
                  if (newIds.every((id) => receivedIds.includes(id))) {
                    clearInterval(interval)
                  }
                })
                .catch(reportErrorToConsole)
            } else {
              clearInterval(interval)
            }
          }, 5000)
        }
      } finally {
        setIsUploading(false)
      }
      return () => {
        clearInterval(interval)
      }
    },
    [closeDialog, documents, financingProgramId, creditApplicationId, refetchFullCreditApp, sendDocuments],
  )

  const awaitingDocuments = useMemo((): DocumentRequired[] => {
    const documentRequired = documentsRequested.filter(
      (item: DocumentRequired) =>
        documents.find(
          (x: Document) =>
            x.applicantType === item.applicantType &&
            x.status !== EDocumentStatus.Deleted &&
            x.status !== EDocumentStatus.Refused &&
            x.typeId === item.typeId &&
            x.subKey === item.subKey,
        ) === undefined,
    )

    return documentRequired
  }, [documentsRequested, documents])

  const navigateSplitDocs = () => {
    let docs: string[]

    if (selectedDocumentIds.length > 0) {
      docs = selectedDocumentIds
    } else {
      docs = documents.filter((x) => x.status === EDocumentStatus.AwaitingApproval).map((d) => d.id!)
    }

    const docIds = encodeURI(docs.join(';'))

    navigate(`/Applications/${financingProgramId}/${creditApplicationId}/split-documents?docIds=${docIds}`)
  }

  const handleSendRequiredDocumentsMessage = () => {
    return sendRequiredDocumentsMessage({ creditApplicationId, financingProgramId })
  }

  const handleQuickMerge = () => {
    if (selectedDocumentIds.length > 1)
      return quickMerge({
        documentIds: selectedDocumentIds,
        creditApplicationId,
        financingProgramId,
      })
        .then(() => setSelectedDocumentIds([]))
        .catch(reportErrorToConsole)
    return null
  }

  const renderDialog = (param: DocumentDialog) => {
    switch (param) {
      case DocumentDialog.DocumentsRequest:
        return (
          <DocumentsRequestDialog
            creditApplicationId={creditApplicationId}
            open
            onCancel={closeDialog}
            onConfirm={handleDocumentRequest}
          />
        )
        break
      case DocumentDialog.EditDocument:
        return (
          <EditDocumentDialog
            open
            onConfirm={handleUpdateDocumentRequest}
            onCancel={closeDialog}
            defaultValue={editRequiredDocument}
          />
        )
        break
      case DocumentDialog.SendDocument:
        return (
          <SendDocumentsDialog
            open
            creditApplicationId={creditApplicationId}
            onCancel={closeDialog}
            onConfirm={sendDocs}
            awaitingDocument={awaitingDocuments}
            isUploading={isUploading}
            fileList={fileList}
            setFileList={setFileList}
          />
        )
        break
      case DocumentDialog.DeleteDocumentRequest:
        return (
          <DeleteDocumentRequestDialog
            data={requiredDocumentToDelete}
            open
            onCancel={closeDeleteDocumentRequestDialog}
            onConfirm={handleDeleteDocumentRequest}
          />
        )
        break
      default:
        return null
    }
  }

  return (
    <>
      <Stack direction="row" spacing={2}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(DocumentDialog.DocumentsRequest)}
          disabled={isCreatingRequiredDocument || editDisabled || !user?.rights.canManageDocuments}
        >
          {t('document.request')}
          {isCreatingRequiredDocument && <CircularProgress size="1rem" sx={{ marginLeft: '1rem' }} />}
        </Button>
        <Button
          variant="outlined"
          startIcon={<SendIcon />}
          onClick={() => setOpenDialog(DocumentDialog.SendDocument)}
          disabled={
            (isUploading && !openDialog) ||
            editDisabled ||
            !(user?.rights.canUploadDocuments || user?.rights.canUploadPrivateDocuments)
          }
        >
          {t('document.send')}
          {isUploading && !openDialog && <CircularProgress size="1rem" sx={{ marginLeft: '1rem' }} />}
        </Button>
        {financingProgramId === EFinancingProgram.Auto && (
          <Button
            variant="outlined"
            disabled={
              isSendingRequiredDocumentsMessage ||
              editDisabled ||
              !user?.rights.canUseMessaging ||
              areAllDocumentsRequiredApproved
            }
            startIcon={<NotificationActiveIcon />}
            onClick={() => handleSendRequiredDocumentsMessage()}
          >
            {isSendingRequiredDocumentsMessage && <CircularProgress size={14} />}
            {!isSendingRequiredDocumentsMessage && t('document.incompleteDocuments')}
          </Button>
        )}
        <Button
          id="btnReviewDocuments"
          variant="outlined"
          startIcon={<ReviewDocumentsIcon />}
          disabled={
            (!documents.some((x) => x.status === EDocumentStatus.AwaitingApproval) &&
              selectedDocumentIds.length === 0) ||
            editDisabled ||
            !user?.rights.canManageDocuments
          }
          onClick={() => navigateSplitDocs()}
        >
          {selectedDocumentIds.length >= 1 ? t('document.reviewSelected') : t('document.splitDocument')}
        </Button>
        <Button
          variant="outlined"
          startIcon={<Merge />}
          disabled={selectedDocumentIds.length < 2 || isQuickMerging}
          onClick={() => handleQuickMerge()}
        >
          {t('document.quickMerge')}
          {isQuickMerging && <CircularProgress size="1rem" sx={{ marginLeft: '1rem' }} />}
        </Button>
      </Stack>
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ borderBottom: 'none' }}>
                <Typography variant="h6" gutterBottom component="div">
                  {t('document.requestedDocument')}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell align="left">Type</TableCell>
              <TableCell align="left">{t('common.requiredFor')}</TableCell>
              {financingProgramId === EFinancingProgram.Personal && (
                <TableCell align="left">{t('common.requiredFrom')}</TableCell>
              )}
              <TableCell align="left">{t('document.receivedOn')}</TableCell>
              <TableCell align="left">{t('document.requestedOn')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          {!isLoading && (
            <TableBody>
              {documentsRequested.map((item: DocumentRequired) => (
                <DocumentTableRow
                  editDisabled={editDisabled}
                  setEditRequiredDocument={setEditRequiredDocument}
                  setOpenDialog={setOpenDialog}
                  documentRequired={item}
                  userRights={user?.rights}
                  documents={documents.filter(
                    (x) =>
                      x.typeId === item.typeId &&
                      x.subKey === item.subKey &&
                      (x.applicantType === item.applicantType ||
                        (isEmpty(x.applicantType) && isEmpty(item.applicantType))),
                  )}
                  key={item.id}
                  handleDelete={(data: DocumentRequired) => {
                    setRequiredDocumentToDelete(data)
                    setOpenDialog(DocumentDialog.DeleteDocumentRequest)
                  }}
                  sendDocs={sendDocs}
                  isUploading={isUploading}
                  financingProgramId={financingProgramId}
                  userCanEditDocument={user?.rights.canManageDocuments === true}
                  handleDocumentChecked={handleDocumentChecked}
                  selectedDocumentIds={selectedDocumentIds}
                  handleUpdateDocumentRequest={handleUpdateDocumentRequest}
                  worksheet={worksheet}
                  showRequiredFromColumn={financingProgramId === EFinancingProgram.Personal}
                />
              ))}

              <TableRow>
                <TableCell sx={{ borderBottom: 'none' }}>
                  <Typography variant="h6">{t('document.otherDocumentsReceived')}</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align="left">Type</TableCell>
                <TableCell />
                {financingProgramId === EFinancingProgram.Personal && (
                  <TableCell align="left">{t('common.requiredFrom')}</TableCell>
                )}
                <TableCell align="left">{t('document.receivedOn')}</TableCell>
                <TableCell align="left">{t('document.requestedOn')}</TableCell>
                <TableCell />
              </TableRow>
              {documents
                .filter(
                  (x) => !documentsRequested.some((y) => y.applicantType === x.applicantType && y.typeId === x.typeId),
                )
                .map((x) => (
                  <DocumentTableRow
                    userRights={user?.rights}
                    editDisabled={editDisabled}
                    key={x?.id}
                    setEditRequiredDocument={setEditRequiredDocument}
                    setOpenDialog={setOpenDialog}
                    documentRequired={
                      {
                        typeId: x?.typeId,
                        note: '',
                        applicantType: x.applicantType,
                        requiredBeforeStep: null,
                        creditApplicationId: x.creditApplicationId,
                        financingProgramId: x.financingProgramId,
                        subKey: x.subKey,
                      } as DocumentRequired
                    }
                    documents={[x] as Document[]}
                    sendDocs={sendDocs}
                    isUploading={isUploading}
                    financingProgramId={financingProgramId}
                    userCanEditDocument={user?.rights.canManageDocuments === true}
                    handleDocumentChecked={handleDocumentChecked}
                    selectedDocumentIds={selectedDocumentIds}
                    handleUpdateDocumentRequest={handleUpdateDocumentRequest}
                    worksheet={worksheet}
                    showRequiredFromColumn={financingProgramId === EFinancingProgram.Personal}
                  />
                ))}
            </TableBody>
          )}
        </Table>
      </TableContainer>
      {renderDialog(openDialog)}
    </>
  )
}

export default DocumentList
